<template>
    <div>
        <!-- Stats Section -->
        <div v-if="showStats">
            <div class="text-center mb-4">
                <div>
                    <h1>{{ $t('trip_planner.total') }} {{ stats.totalDistance }}{{ $t('trip_planner.km') }}</h1>
                    <h1>{{ $t('trip_planner.about') }} {{ stats.totalDuration }} {{ $t('trip_planner.hours') }}</h1>
                    <h1 v-if="totalBreakTime > 0" class="text-yellow-600">{{ $t('trip_planner.break_time') }}: {{ formatBreakTime(totalBreakTime) }}</h1>
                </div>
            </div>
            <hr />
        </div>

        <div class="route-details-body" :class="customClass" :style="{ '--line-bottom': lineBottom }">
          <!-- Start Point -->
          <div class="route-waypoint-main">
            <div class="route-icon-circle route-icon-start">
                <BaseIcon icon="map-marker" class="text-white" style="font-size: 16px;" />
            </div>
            <div class="route-point bg-blue-50">
                <div class="flex-1">
                <div class="text-xxs text-blue-600 font-semibold uppercase">{{ startLabel || $t('trip_planner.trip_start_label') }}</div>
                <div class="text-xs text-gray-800">{{ startAddress }}</div>
                </div>
            </div>
          </div>

          <!-- Intermediate Stops with Breaks -->
          <div v-for="(stop, index) in stopsWithBreaks" :key="'stop-' + index" class="route-stop-group">
            <!-- Stop -->
            <div class="route-waypoint-main">
                <div class="route-icon-circle route-icon-stop">
                <BaseIcon icon="map-marker" class="text-white" style="font-size: 14px;" />
                </div>
                <div class="route-point bg-blue-50">
                <div class="flex-1">
                    <div class="text-xxs text-blue-600 font-semibold uppercase">{{ stopLabel || $t('trip_planner.stop_label') }} {{ stop.stopNumber }}</div>
                    <div class="text-xs text-gray-800">{{ stop.address }}</div>
                    <div v-if="stop?.leg" class="text-xs text-gray-600 font-semibold mt-1">
                    {{ stop.leg.distance?.text }}, {{ $te('trip_planner.about') ? $t('trip_planner.about') : 'About' }} {{ stop.leg.duration?.text }}
                    </div>
                </div>
                </div>
            </div>

            <!-- Breaks for this stop -->
            <div v-if="stop.breaks && stop.breaks.length > 0" class="breaks-container">
                <div v-for="(breakItem, bIndex) in stop.breaks" :key="'break-' + bIndex" class="route-break-branch">
                <div class="break-branch-line"></div>
                <div class="break-point bg-amber-50">
                    <div class="flex-1">
                    <div class="flex items-center gap-2">
                        <div class="text-xxs text-amber-600 font-semibold uppercase">{{ breakItem.breakType || 'Break' }}</div>
                        <div class="text-xs font-semibold text-amber-700">{{ breakItem.breakDuration }} min</div>
                    </div>
                    <div v-if="breakItem.note" class="text-xs text-gray-600 italic mt-1">{{ breakItem.note }}</div>
                    </div>
                </div>
                </div>
            </div>
          </div>

          <!-- End Point -->
          <div class="route-waypoint-main">
            <div class="route-connector-line"></div>
            <div class="route-icon-circle route-icon-end">
                <BaseIcon icon="map-marker" class="text-white" style="font-size: 16px;" />
            </div>
            
            <div class="route-point bg-amber-50">
              <div class="flex-1">
              <div class="text-xxs text-amber-600 font-semibold uppercase">{{ endLabel || $t('trip_planner.final_destination_label') }}</div>
              <div class="text-xs text-gray-800">{{ endAddress }}</div>
              <div v-if="optimizedPath[optimizedPath.length - 1]" class="text-xs text-gray-600 font-semibold mt-1">
                {{ optimizedPath[optimizedPath.length - 1].distance?.text }}, {{ $te('trip_planner.about') ? $t('trip_planner.about') : 'About' }} {{ optimizedPath[optimizedPath.length - 1].duration?.text }}
              </div>
              </div>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
export default {
  name: 'RoutePreviewTimeline',
  
  props: {
    waypoints: {
      type: Array,
      default: () => []
    },
    showStats: {
      type: Boolean,
      default: false
    },
    totalBreakTime: {
      type: Number,
      default: 0
    },
    startLabel: {
      type: String,
      default: ''
    },
    endLabel: {
      type: String,
      default: ''
    },
    stopLabel: {
      type: String,
      default: ''
    },
    customClass: {
      type: [String, Object, Array],
      default: ''
    },
    fallbackStartAddress: {
      type: String,
      default: ''
    },
    fallbackEndAddress: {
      type: String,
      default: ''
    }
  },

  computed: {
    optimizedPath() {
      return this.$store.getters.getOptimizedPath ?? [];
    },

    stats() {
      let fullData = this.optimizedPath;
      let totalDistance = 0;
      let totalDuration = 0;
      fullData.forEach((item) => {
        totalDistance += +item.distance.value;  // Sum all distances
        totalDuration += +item.duration.value;  // Sum all durations
      });

      totalDistance = (totalDistance / 1000).toFixed(1);  // meters to km
      totalDuration = (totalDuration / 3600).toFixed(1);  // seconds to hours
      
      return { totalDistance: totalDistance, totalDuration: totalDuration };
    },

    startAddress() {
      return this.waypoints?.[0]?.address || this.fallbackStartAddress || '';
    },

    endAddress() {
      return this.waypoints?.[this.waypoints?.length - 1]?.address || this.fallbackEndAddress || '';
    },

    lineBottom() {
      const stopsCount = this.stopsWithBreaks.length;
      // Adjust bottom based on number of stops to ensure line extends properly
      if (stopsCount === 0) {
        return '50px';
      }
      // Calculate based on stops and breaks
      const baseBottom = 85;
      return `${baseBottom}px`;
    },

    stopsWithBreaks() {
      if (!this.waypoints || this.waypoints.length <= 2) {
        return [];
      }
      
      const legs = this.optimizedPath;
      const intermediate = this.waypoints.slice(1, -1);
      const stops = [];
      let stopNumber = 1;
      let currentStop = null;
      
      intermediate.forEach((waypoint) => {
        if (waypoint.type === 'destination') {
          // If there's a current stop, push it to stops array
          if (currentStop) {
            stops.push(currentStop);
          }
          
          // Find the destination index among all destinations (not just intermediate)
          const allDestinations = this.waypoints.filter(w => w.type === 'destination');
          const destinationIndex = allDestinations.indexOf(
            allDestinations.find(d => d.address === waypoint.address)
          );
          
          // Create new stop with leg data
          currentStop = {
            ...waypoint,
            stopNumber: stopNumber++,
            breaks: []
          };
          
          // Add leg data if available (legs[destinationIndex - 1] connects to this destination)
          if (destinationIndex > 0 && legs[destinationIndex - 1]) {
            currentStop.leg = { ...legs[destinationIndex - 1] };
          }
        } else if (waypoint.type === 'break' && currentStop) {
          // Add break to current stop
          currentStop.breaks.push(waypoint);
        }
      });
      
      // Don't forget to push the last stop
      if (currentStop) {
        stops.push(currentStop);
      }
      
      return stops;
    }
  },

  methods: {
    formatBreakTime(minutes) {
      if (minutes < 60) {
        return `${minutes} min`;
      }
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${mins} min`;
    }
  }
};
</script>

<style scoped>
.route-details-body {
  padding: 20px 0px 0px 45px;
  position: relative;
}

/* Main vertical dashed line */
.route-details-body::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 50px;
  bottom: var(--line-bottom, 50px);
  width: 2px;
  background-image: linear-gradient(to bottom, #9CA3AF 50%, transparent 50%);
  background-size: 2px 12px;
  z-index: 0;
}

.route-stop-group {
  position: relative;
  margin: 0;
  padding: 0;
}

.route-waypoint-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 24px;
}

/* Icon circles */
.route-icon-circle {
  position: absolute;
  left: -46px;
  top: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.route-icon-start {
  background: #3b82f6;
}

.route-icon-end {
  background: #F59E0B;
}

.route-icon-stop {
  background: #3b82f6;
}

.route-point {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  margin: 0;
  background: white;
  border: 1px solid #E5E7EB;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  flex: 1;
}

.route-point::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 20px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 12px solid white;
  filter: drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.05));
}

.route-point:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateX(4px);
}

.route-point.bg-blue-50 {
  background: white;
  border-left: 4px solid #3b82f6;
}

.break-point.bg-amber-50 {
  background: white;
  border-left: 4px solid #F59E0B;
  padding: 16px 20px;
  border-radius: 12px;
}

.route-point.bg-green-50 {
  background: white;
  border-left: 4px solid #10b981;
}

.route-point.bg-red-50 {
  background: white;
  border-left: 4px solid #ef4444;
}
.route-point.bg-amber-50 {
  background: white;
  border-left: 4px solid #F59E0B;
}

.route-point > .flex-1 {
  min-width: 0;
}

.route-point .text-xxs {
  font-size: 11px;
  letter-spacing: 0.05em;
  font-weight: 700;
  margin-bottom: 4px;
}

.route-point .text-xs {
  font-size: 14px;
  color: #374151;
  font-weight: 400;
  line-height: 1.5;
}

.route-connector-line {
  display: none;
}

/* Breaks container */
.breaks-container {
  margin-left: 0;
  position: relative;
}

.route-break-branch {
  position: relative;
  margin-bottom: 12px;
}


.break-branch-line {
  content: "";
  position: absolute;
  top: -50%;
  left: 50%;
  width: 2px;
  height: 50%;
  background-color: #F59E0B;
  transform: translateX(-50%);
}

.break-point {
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 12px 16px;
}

.text-xxs {
  font-size: 0.65rem;
  line-height: 1rem;
  letter-spacing: 0.025em;
}

.text-amber-600 {
  color: #d97706;
}
</style>
